function isReverseGate(gateName: string) {
  return gateName.startsWith('-');
}

function isNegatedApplicationId(applicationId: string) {
  return applicationId.startsWith('-');
}

/**
 * Returns true when isUngatedFor returns true for all valid gates within gates.
 *
 * gates can contain reverse gates in the format of "-GATE_NAME" and those should be ignored.
 */
export function getIsUngatedToAll({
  gates,
  isUngatedFor,
}: {
  gates?: string[];
  isUngatedFor: (gateName: string) => boolean;
}) {
  if (!gates || gates.length === 0) {
    return true;
  }

  return gates
    .filter((gateName) => !isReverseGate(gateName))
    .every(isUngatedFor);
}

/**
 * Returns true when isUngatedFor returns false for all valid reverse gates ("-GATE_NAME") within gates.
 *
 * gates can contain gates in the format of "GATE_NAME" and those should be ignored.
 */
export function getIsGatedToAll({
  gates,
  isUngatedFor,
}: {
  gates?: string[];
  isUngatedFor: (gateName: string) => boolean;
}) {
  if (!gates || gates.length === 0) {
    return true;
  }

  return gates
    .filter(isReverseGate)
    .map((gateName) => gateName.replace('-', ''))
    .every((gateName) => !isUngatedFor(gateName));
}

/**
 * Returns true when:
 *  - applicationIds has no positive (non-negated) entries OR
 *  - the current application id is included in the positive applicationIds
 *
 * applicationIds can contain negated entries in the format of "-APPLICATION_ID" and those should be ignored.
 */
export function getHasApplicationId({
  applicationIds,
  currentApplicationId,
}: {
  applicationIds?: string[];
  currentApplicationId: string;
}) {
  if (!applicationIds || applicationIds.length === 0) {
    return true;
  }

  const positiveApplicationIds = applicationIds.filter(
    (id) => !isNegatedApplicationId(id)
  );

  return (
    positiveApplicationIds.length === 0 ||
    positiveApplicationIds.includes(currentApplicationId)
  );
}

/**
 * Returns true when:
 *  - applicationIds has no negated ("-APPLICATION_ID") entries OR
 *  - the current application id is NOT included in the negated applicationIds
 *
 * applicationIds can contain positive entries and those should be ignored.
 */
export function getNotHasApplicationId({
  applicationIds,
  currentApplicationId,
}: {
  applicationIds?: string[];
  currentApplicationId: string;
}) {
  if (!applicationIds || applicationIds.length === 0) {
    return true;
  }

  return applicationIds
    .filter(isNegatedApplicationId)
    .map((id) => id.replace('-', ''))
    .every((id) => id !== currentApplicationId);
}
